<template>
  <v-card align="center" justify="center" light min-width="42vw" outlined>
    <v-card-title :style="`background-color: ${(trade.netProfit > 0)?'#00F784':'#ff6500'}`">
      <v-list-item>
        <v-list-item-title v-if="trade.netProfit > 0">
          <h2 style="color: #111111">Winning trade of {{ trade.asset.ticker }}
            <v-chip v-if="trade.automaticallyAdded" class="ml-3" small>Automated</v-chip>
          </h2>
        </v-list-item-title>
        <v-list-item-title v-else>
          <h2 style="color: #FFFFFF">Losing trade of {{ trade.asset.ticker }}
            <v-chip v-if="trade.automaticallyAdded" class="ml-2" small>Automated</v-chip>
          </h2>
        </v-list-item-title>
        <v-spacer/>
        <v-list-item-action>
          <v-icon class="icon" icon @click="closeWindow">fa-times</v-icon>
        </v-list-item-action>
      </v-list-item>
    </v-card-title>
    <v-card-text class="px-2">
      <v-container fluid>
        <v-row>
          <v-col>
            <p class="label">Asset</p>
            <p>{{ trade.asset.assetName }}</p>
          </v-col>
          <v-col>
            <p class="label">Broker Account</p>
            <p>{{ trade.brokerAccount.broker.name }} - {{ trade.brokerAccount.identifier }}</p>
          </v-col>
          <v-col>
            <p class="label">Side</p>
            <p>{{ trade.side }}</p>
          </v-col>
          <v-col>
            <p class="label">Mood</p>
            <v-chip :color="getMoodColor(trade.mood)" dark outlined small>
              <v-icon left>{{ getMoodIcon(trade.mood) }}</v-icon>
              {{ trade.mood }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row style="border-top: 1px #d2d7e1 solid;">
          <v-col>
            <p class="label">Open Time</p>
            <p>{{
                `${trade.openTime.getDate()}/${trade.openTime.getMonth() + 1}/${trade.openTime.getFullYear().toString().substr(-2)} ${String(trade.openTime.getHours()).padStart(2, '0')}:${String(trade.openTime.getMinutes()).padStart(2, '0')}`
              }}</p>
            <p class="label">Entry Price</p>
            <p>{{ trade.entryPrice }}</p>
            <p class="label">Gross Profit</p>
            <p :style="`color: ${getNetProfitColor(trade.netProfit + trade.commisions)}`"><b>{{ (trade.netProfit + trade.commisions) }}</b></p>
            <p class="label" v-if="trade.contracts !== null || trade.lots !== null">Contracts / Lots</p>
            <p v-if="trade.contracts !== null || trade.lots !== null">{{ (trade.contracts !== null)? `${trade.contracts} contract(s)` : `${trade.lots} lot(s)`}}</p>
          </v-col>
          <v-col>
            <p class="label">Close Time</p>
            <p>{{`${trade.closeTime.getDate()}/${trade.closeTime.getMonth() + 1}/${trade.closeTime.getFullYear().toString().substr(-2)} ${String(trade.closeTime.getHours()).padStart(2, '0')}:${String(trade.closeTime.getMinutes()).padStart(2, '0')}` }}</p>
            <p class="label">Close Price</p>
            <p>{{ trade.closePrice }}</p>
            <p class="label">Net Profit</p>
            <p :style="`color: ${getNetProfitColor(trade.netProfit)}`"><b>{{ trade.netProfit }}</b></p>
            <p class="label" v-if="trade.mae !== null  || trade.swap !== null">{{ (trade.mae !== null) ? 'MAE' : 'Swap' }}</p>
            <p v-if="trade.mae !== null || trade.swap !== null">{{ (trade.mae !== null)? trade.mae : trade.swap }}</p>
          </v-col>
          <v-col>
            <p class="label">Trade Duration</p>
            <p>{{ getTradeTime(trade.tradeTime) }}</p>
            <p class="label">Stop Loss Price</p>
            <p>{{ trade.stopLossPrice }}</p>
            <p class="label">Commissions</p>
            <p>{{ trade.commisions }}</p>
            <p class="label" v-if="trade.mfe !== null">MFE</p>
            <p v-if="trade.mfe !== null">{{ trade.mfe }}</p>
          </v-col>
          <v-col>
            <p class="label">Strategy</p>
            <p v-if="trade.strategy != null">{{ trade.strategy.name }}</p>
            <p v-else>None</p>
            <p class="label">Pip/Point Value</p>
            <p>{{ trade.pipValue.toFixed(2) }}</p>
            <p class="label">Trade Risk</p>
            <p style="color: #c93f00">{{ (trade.riskPercentage != null && trade.riskPercentage !== 0) ? `${trade.monetaryRisk.toFixed(2)} ${trade.brokerAccount.currency} (${(trade.riskPercentage * 100).toFixed(3)}%)` : trade.monetaryRisk.toFixed(2) }}</p>
            <p class="label" v-if="trade.overnight !== null">Overnight</p>
            <p v-if="trade.overnight !== null">{{ (trade.overnight)? 'Yes' : 'No' }}</p>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "FullTrade",
  props: {
    trade: Object,
  },
  methods: {
    getAccountTypeColor(type) {
      return (type.toUpperCase() === "LIVE") ? '#00a575' : '#008cff';
    },
    getTradeSideColor(side) {
      return (side.toUpperCase() === "BUY") ? '#00a575' : '#ff8800';
    },
    getNetProfitColor(netProfit) {
      return (netProfit > 0) ? '#00a575' : '#ff6b55';
    },
    getMoodColor(mood) {
      switch (mood) {
        case 'Relaxed':
          return "#4d6556";
        case 'Cautious':
          return "#386538";
        case 'Concentrated':
          return "#376563";
        case 'Confident':
          return "#376563";
        case 'Excited':
          return "#28655c";
        case 'Anxious':
          return "#28655c";
        case 'Happy':
          return "#0f6500";
        case 'Optimistic':
          return "#1d5465";
        case 'Aggressive':
          return "#651540";
        case 'Angry':
          return "#650700";
        case 'Bored':
          return "#65412e";
        case 'Mad':
          return "#651540";
        case 'Nervous':
          return "#651540";
        case 'Sad':
          return "#65412e";
        case 'Upset':
          return "#651540";
        case 'None':
          return "#555555";
        default:
          return "#651540";
      }
    },
    getMoodIcon(mood) {
      switch (mood) {
        case 'Relaxed':
          return "fa-laugh-wink";
        case 'Cautious':
          return "fa-smile";
        case 'Concentrated':
          return "fa-meh-blank";
        case 'Confident':
          return "fa-smile-beam";
        case 'Excited':
          return "fa-grin-tongue";
        case 'Happy':
          return "fa-grin-beam";
        case 'Optimistic':
          return "fa-grin-stars";
        case 'Aggressive':
          return "fa-meh-rolling-eyes";
        case 'Angry':
          return "fa-angry";
        case 'Bored':
          return "fa-frown";
        case 'Mad':
          return "fa-angry";
        case 'Nervous':
          return "fa-grimace";
        case 'Sad':
          return "fa-sad-tear";
        case 'Upset':
          return "fa-frown-open";
        case 'None' :
          return "fa-ban";
        default:
          return "fa-flushed";
      }
    },
    getTradeTime(duration) {
      let message = (duration.days > 0) ? `${duration.days}d` : '';
      message = `${message} ${(duration.hours > 0) ? `${duration.hours}h` : ''}`;
      message = `${message} ${(duration.minutes > 0) ? `${duration.minutes}m` : ''}`;
      message = `${message} ${(duration.seconds > 0) ? `${duration.seconds}s` : ''}`;
      return message;
    },
    closeWindow() {
      this.$emit('closeWindow');
    },
  }
}
</script>

<style scoped>
  p {
    color: #111111;
  }

  .label {
    color: #50565e;
    size: 14px;
    margin-bottom: 3px;
    margin-top: 14px;
  }

  .disable-events {
    pointer-events: none;
  }
</style>
